import React, { useMemo, useState } from "react";
import { GlassWater, Loader2, RefreshCw } from "lucide-react";
import ProducaoSalaoBusca from "../../../components/ProducaoSalaoBusca";
import { matchesMesaComandaSearch } from "../../../utils/matchesMesaComandaSearch";
import { useProducaoBebidas } from "../context/ProducaoBebidasContext";
import type { BebidaMesaTicket as BebidaMesaTicketType } from "../utils/agruparBebidasPorMesa";
import BebidaMesaTicket from "./BebidaMesaTicket";

type ViewTab = "fila" | "prontas";

function ticketMatchesSearch(
  ticket: BebidaMesaTicketType,
  query: string,
): boolean {
  const q = query.trim();
  if (!q) return true;
  if (ticket.labels.badge.toLowerCase().includes(q.toLowerCase())) return true;
  if (ticket.labels.mesa?.toLowerCase().includes(q.toLowerCase())) return true;
  if (ticket.labels.comandaLabel?.toLowerCase().includes(q.toLowerCase())) {
    return true;
  }
  return ticket.linhas.some((linha) => matchesMesaComandaSearch(linha, q));
}

const ProducaoBebidasContent: React.FC = () => {
  const {
    mesasPending,
    mesasDone,
    loading,
    refreshing,
    busyIds,
    refreshNow,
    handleFinalizar,
    totalAtivos,
  } = useProducaoBebidas();

  const [viewTab, setViewTab] = useState<ViewTab>("fila");
  const [searchQuery, setSearchQuery] = useState("");

  const mesasFiltradas = useMemo(() => {
    return {
      fila: mesasPending.filter((t) => ticketMatchesSearch(t, searchQuery)),
      prontas: mesasDone.filter((t) => ticketMatchesSearch(t, searchQuery)),
    };
  }, [mesasPending, mesasDone, searchQuery]);

  const tickets =
    viewTab === "fila" ? mesasFiltradas.fila : mesasFiltradas.prontas;
  const showFinalizar = viewTab === "fila";

  if (loading) {
    return (
      <main className="flex h-[calc(100vh-4rem)] items-center justify-center">
        <Loader2 size={32} className="animate-spin text-sky-600" />
      </main>
    );
  }

  return (
    <main className="flex h-[calc(100vh-4rem)] flex-col bg-neutral-100">
      <div className="flex flex-wrap items-center gap-2 border-b border-neutral-200 bg-white px-3 py-2.5 sm:justify-between sm:px-4 sm:py-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <GlassWater size={20} className="shrink-0 text-sky-600" />
          <h1 className="truncate text-base font-bold text-neutral-800 sm:text-lg">
            KDS Bebidas
          </h1>
          <span className="shrink-0 rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-semibold text-sky-800">
            {totalAtivos} {totalAtivos === 1 ? "ativa" : "ativas"}
          </span>
          <span className="hidden text-xs text-neutral-500 lg:inline">
            Agrupado por mesa · paisagem
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <ProducaoSalaoBusca value={searchQuery} onChange={setSearchQuery} />
          <button
            type="button"
            onClick={() => void refreshNow()}
            disabled={refreshing}
            aria-label="Atualizar"
            className="flex shrink-0 items-center gap-1.5 rounded-lg border border-neutral-300 px-3 py-2 text-sm text-neutral-600 transition-colors hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {refreshing ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <RefreshCw size={16} />
            )}
            <span className="hidden sm:inline">
              {refreshing ? "Atualizando..." : "Atualizar"}
            </span>
          </button>
        </div>
      </div>

      <div className="flex gap-1.5 border-b border-neutral-200 bg-white px-3 py-2 sm:px-4">
        <button
          type="button"
          onClick={() => setViewTab("fila")}
          className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors sm:text-sm ${
            viewTab === "fila"
              ? "bg-neutral-800 text-white"
              : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
          }`}
        >
          Fila
          <span
            className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
              viewTab === "fila"
                ? "bg-white/20 text-white"
                : "bg-amber-200 text-amber-800"
            }`}
          >
            {mesasFiltradas.fila.length}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setViewTab("prontas")}
          className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors sm:text-sm ${
            viewTab === "prontas"
              ? "bg-neutral-800 text-white"
              : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
          }`}
        >
          Prontas
          <span
            className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
              viewTab === "prontas"
                ? "bg-white/20 text-white"
                : "bg-emerald-200 text-emerald-800"
            }`}
          >
            {mesasFiltradas.prontas.length}
          </span>
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3 sm:p-4">
        {tickets.length === 0 ? (
          <div className="flex h-full min-h-[240px] flex-col items-center justify-center text-neutral-400">
            <GlassWater size={48} className="mb-2 opacity-30" />
            <p className="text-sm font-medium">
              {searchQuery.trim()
                ? "Nenhum resultado para esta busca"
                : viewTab === "fila"
                  ? "Nenhuma bebida na fila"
                  : "Nenhuma bebida pronta"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
            {tickets.map((ticket) => (
              <BebidaMesaTicket
                key={ticket.key}
                ticket={ticket}
                busyIds={busyIds}
                showFinalizar={showFinalizar}
                onFinalizar={(ids) => void handleFinalizar(ids)}
              />
            ))}
          </div>
        )}
      </div>
    </main>
  );
};

export default ProducaoBebidasContent;
